import React, { useState } from 'react';
import { PageHeader, Card, FormGroup, Select, Button, DataTable, ActionButtons, SectionDivider, Input } from '../../components/common/UIComponents';
import { BudgetController, LedgerController } from '../../controllers';
import { useAsyncData } from '../../hooks/useAsyncData';

export default function BudgetPage() {
  const { data: records, loading, error, reload } = useAsyncData(() => BudgetController.getAll());
  const { data: ledgers } = useAsyncData(() => LedgerController.getAll());
  const [form, setForm]   = useState({ ledgerId: '', budgetAmount: '' });
  const [editing, setEditing] = useState(null);
  const [saving, setSaving]   = useState(false);

  const totalBudget   = records.reduce((s, r) => s + (r.budgetAmount || 0), 0);
  const totalExpenses = records.reduce((s, r) => s + (r.expenses || 0), 0);

  const handleSubmit = async () => {
    if (!form.budgetAmount) return;
    setSaving(true);
    try {
      const payload = { ledgerId: parseInt(form.ledgerId), budgetAmount: parseFloat(form.budgetAmount) };
      if (editing) { await BudgetController.update(editing, { budgetAmount: payload.budgetAmount }); setEditing(null); }
      else { await BudgetController.add(payload); }
      setForm({ ledgerId: '', budgetAmount: '' });
      reload();
    } catch(e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const columns = [
    { key: 'id', label: '#' },
    { key: 'ledgerName',    label: 'Ledger',   render: r => r.ledgerName || r.ledger },
    { key: 'openingBalance',label: 'Opening',  render: r => `₹${(r.openingBalance||0).toLocaleString('en-IN')}` },
    { key: 'budgetAmount',  label: 'Budget',   render: r => <span className="td-amount credit">₹{(r.budgetAmount||0).toLocaleString('en-IN')}</span> },
    { key: 'expenses',      label: 'Expenses', render: r => <span className="td-amount debit">₹{(r.expenses||0).toLocaleString('en-IN')}</span> },
    { key: 'difference',    label: 'Difference', render: r => { const d = (r.budgetAmount||0)-(r.expenses||0); return <span className={`td-amount ${d>=0?'credit':'debit'}`}>₹{d.toLocaleString('en-IN')}</span>; } },
    { key: 'actions', label: 'Actions', render: row => (
      <ActionButtons
        onEdit={() => { setForm({ ledgerId: row.ledgerId||'', budgetAmount: (row.budgetAmount||'').toString() }); setEditing(row.id); }}
        onDelete={async () => { try { await BudgetController.delete(row.id); reload(); } catch(e){alert(e.message);} }}
      />
    )},
  ];

  return (
    <div className="fade-in">
      <PageHeader title="Budget" description="Set budget amounts for each expense ledger" />
      <div className="stat-grid" style={{ marginBottom: 20 }}>
        <div className="stat-card blue"><div className="stat-icon blue">📊</div><div className="stat-value">₹{(totalBudget/100000).toFixed(1)}L</div><div className="stat-label">Total Budget</div></div>
        <div className="stat-card red"><div className="stat-icon red">💸</div><div className="stat-value">₹{totalExpenses.toLocaleString('en-IN')}</div><div className="stat-label">Total Expenses</div></div>
        <div className="stat-card green"><div className="stat-icon green">✅</div><div className="stat-value">₹{(totalBudget-totalExpenses).toLocaleString('en-IN')}</div><div className="stat-label">Remaining</div></div>
        <div className="stat-card amber"><div className="stat-icon amber">📋</div><div className="stat-value">{records.length}</div><div className="stat-label">Entries</div></div>
      </div>
      <Card title={editing ? 'Edit Budget' : 'Add Budget'} style={{ marginBottom: 20 }}>
        <div className="form-grid form-grid-2" style={{ marginBottom: 14 }}>
          <FormGroup label="Select Ledger" required>
            <Select value={form.ledgerId} onChange={e=>setForm(p=>({...p,ledgerId:e.target.value}))}
              options={ledgers.map(l=>({value:l.id,label:l.ledgerName||l.name}))} placeholder="Select Ledger" />
          </FormGroup>
          <FormGroup label="Budget Amount" required>
            <Input type="number" placeholder="Enter Amount" value={form.budgetAmount} onChange={e=>setForm(p=>({...p,budgetAmount:e.target.value}))} />
          </FormGroup>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <Button onClick={handleSubmit} disabled={saving}>{saving?'Saving…':editing?'Update':'Save'}</Button>
          {editing && <Button variant="ghost" onClick={()=>{setForm({ledgerId:'',budgetAmount:''});setEditing(null);}}>Cancel</Button>}
        </div>
      </Card>
      <SectionDivider />
      {loading && <div style={{padding:20,color:'#78909c'}}>Loading…</div>}
      {error   && <div style={{padding:20,color:'#c62828'}}>Error: {error}</div>}
      {!loading && <DataTable title="Budget Details" columns={columns} data={records} />}
    </div>
  );
}
